import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { idenfyNameValidator, nameValidator } from 'src/app/core/helpers/custom-name-validator.helper';
import { forbidUppercase } from '../../Helper/custom-form-validators';
import { AccountService } from '../../services/account.service';
import { CommonService } from '../../services/common.service';
import { NgClass, NgIf } from '@angular/common';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
    imports: [FormsModule, ReactiveFormsModule, NgClass, NgIf]
})
export class ProfileComponent implements OnChanges {
  @Input() showMailVerificationMenu = true;
  submitted = false;
  profilForm!: FormGroup;
  @Output() closeModal = new EventEmitter();
  @Output() openModal = new EventEmitter();
  isDisabled = false;
  @Input() isCancelOption = false;
  apiProcessing = false;
  is_Triggered: boolean = false;

  signature!: string;
  @Input() userData: any;
  /** kyc_verified from the header's cached KYC status: 0 pending, 1 verified, 2 failed, 3 reviewing, 4 expired */
  @Input() kycVerified: number | undefined;
  @Output() dataEvent = new EventEmitter<any>();

  /** The legal name is frozen once identity is verified; the server enforces this too. */
  get isNameLocked(): boolean {
    return this.kycVerified === 1;
  }

  /**
   * constructor
   */
  constructor(
    private fb: FormBuilder,
    public commonService: CommonService,
    private toastrService: ToastrService,
    private accountService: AccountService
  ) {
  }

  ngOnInit() {
    this.initiatForm()
    this.patchUserData()
  }

  initiatForm() {
    this.profilForm = this.fb.group({
      first_name: ['', [Validators.required, idenfyNameValidator()]],
      last_name: ['', [Validators.required, idenfyNameValidator()]],
      email: ['', [Validators.required, Validators.email, forbidUppercase()]],
      // Optional: a user may have no display name at all
      display_name: ['', [Validators.maxLength(15), nameValidator(true, false)]]
    })
  }

  get f() { return this.profilForm?.controls as { [key: string]: AbstractControl }; }

  /** Save stays disabled until something the user can actually edit has changed. */
  get hasChanges(): boolean {
    if (!this.profilForm || !this.userData) return false;

    const value = this.profilForm.value;
    const changed = (formValue: string, savedValue: string) => (formValue ?? '').trim() !== (savedValue ?? '');

    if (!this.isNameLocked &&
      (changed(value.first_name, this.userData.first_name) || changed(value.last_name, this.userData.last_name))) {
      return true;
    }

    return changed(value.display_name, this.userData.display_name);
  }

  onSubmit() {
    if (this.profilForm.invalid) {
      this.profilForm.markAllAsTouched()
      this.toastrService.error('Please file all fiels')
    } else {
      const displayName = (this.profilForm.get('display_name')?.value ?? '').trim();
      // The API disallows an empty display name, so an existing one cannot be cleared here
      if (!displayName && this.userData?.display_name) {
        this.f['display_name'].setErrors({ invalidName: 'Display name cannot be removed once set.' });
        this.f['display_name'].markAsTouched();
        return;
      }

      this.apiProcessing = true;
      const payload: Record<string, unknown> = {}
      // Sent only when present — the API rejects an empty string
      if (displayName) {
        payload['display_name'] = displayName;
      }
      // Only sent while the name is still editable — the server rejects them once KYC is verified
      if (!this.isNameLocked) {
        payload['first_name'] = this.profilForm.get('first_name')?.value?.trim();
        payload['last_name'] = this.profilForm.get('last_name')?.value?.trim();
      }
      this.accountService.updateUser(payload).subscribe({
        next: (res: any) => {
          this.toastrService.success('Profile updated successfully')
          this.dataEvent.emit({data:res?.data,updateKey:'display_name'});
          // Back to pristine so the modal repopulates from the refreshed user on next open
          this.profilForm.markAsPristine();
          this.closeProfileModal();
          this.apiProcessing = false;
        },
        error: (error: HttpErrorResponse) => {
          this.apiProcessing = false;
          this.toastrService.error('Error updating profile')
        }
      })
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.patchUserData()
  }

  /**
   * Refills the form from the parent's user object. Skipped once the user starts editing, so an
   * unrelated @Input() change cannot wipe a half-typed name. Also runs before the form exists
   * (ngOnChanges fires ahead of ngOnInit), hence the form guard.
   */
  private patchUserData(): void {
    if (this.userData && this.profilForm?.pristine) {
      this.profilForm.patchValue(this.userData);
    }
  }

  /**
   * Closes profile modal
   */
  closeProfileModal() {
    this.closeModal.emit();
  }

}
